/**
 * MarketingApp — public site for taxsolutionindia.in
 *
 * Structure updated Feb 2026 to use the new luxury-editorial design under
 * `/pages/marketing/`. Blog, Calculator and Useful-Links pages have been
 * removed per the firm's directive.
 *
 * Host-triggering rules live in `isMarketingHost()` — the marketing app only
 * activates on the taxsolutionindia.in host (or `?marketing=1` for preview).
 */
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";

import MarketingLayout from "@/pages/marketing/MarketingLayout";
import MktHome from "@/pages/marketing/Home";
import MktAbout from "@/pages/marketing/About";
import MktServices from "@/pages/marketing/Services";
import MktTestimonials from "@/pages/marketing/Testimonials";
import MktContact from "@/pages/marketing/Contact";

function ScrollToTop() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const id = hash.replace("#", "");
            const el = document.getElementById(id);
            if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); return; }
        }
        window.scrollTo({ top: 0, behavior: "auto" });
    }, [pathname, hash]);
    return null;
}

/**
 * Determines whether the visitor is on the marketing domain.
 * - Production: hostname contains `taxsolutionindia`, OR
 * - Preview / local dev: explicit override via `?marketing=1` (saved in
 *   sessionStorage so deep links and refreshes keep working).
 */
export function isMarketingHost() {
    if (typeof window === "undefined") return false;
    const host = window.location.hostname.toLowerCase();
    if (host.includes("taxsolutionindia")) return true;
    const params = new URLSearchParams(window.location.search);
    if (params.get("marketing") === "1") {
        try { sessionStorage.setItem("ts_marketing", "1"); } catch { /* noop */ }
        return true;
    }
    if (params.get("marketing") === "0") {
        try { sessionStorage.removeItem("ts_marketing"); } catch { /* noop */ }
        return false;
    }
    try { return sessionStorage.getItem("ts_marketing") === "1"; } catch { return false; }
}

export default function MarketingApp() {
    useEffect(() => {
        document.title = "Tax Solution India — Chartered & Tax Advisory · Dibrugarh";
    }, []);

    return (
        <BrowserRouter>
            <ScrollToTop />
            <Routes>
                <Route element={<MarketingLayout />}>
                    <Route path="/"             element={<MktHome />} />
                    <Route path="/about"        element={<MktAbout />} />
                    <Route path="/services"     element={<MktServices />} />
                    <Route path="/testimonials" element={<MktTestimonials />} />
                    <Route path="/contact"      element={<MktContact />} />
                    <Route path="*"             element={<Navigate to="/" replace />} />
                </Route>
            </Routes>
            <Toaster position="top-right" richColors closeButton toastOptions={{ duration: 3000 }} />
        </BrowserRouter>
    );
}
